import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  onIdTokenChanged,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../firebaseClient';
import { setAuthToken, setAuthTokenRefresher, setUnauthorizedHandler } from '../utils/apiClient';
import { setSentryUser } from '../utils/sentry';
import { clearTrialInterstitialAcknowledgements } from '../components/TrialExpiredInterstitial';
import { clearOnboardingInProgress } from '../utils/signupFlow';
import { clearTrialChoice } from '../utils/planActions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      if (fbUser) {
        const idToken = await fbUser.getIdToken();
        setUser(fbUser);
        setToken(idToken);
        setAuthToken(idToken);
        setSentryUser(fbUser.uid);
      } else {
        setUser(null);
        setToken('');
        setAuthToken('');
        setSentryUser(null);
      }
      setLoading(false);
    });
    setAuthTokenRefresher(async () => {
      if (!auth.currentUser) return '';
      const idToken = await auth.currentUser.getIdToken(true);
      setToken(idToken);
      setAuthToken(idToken);
      return idToken;
    });
    setUnauthorizedHandler(async () => {
      await fbSignOut(auth);
      clearTrialInterstitialAcknowledgements();
      clearOnboardingInProgress();
      clearTrialChoice();
      setUser(null);
      setToken('');
      setAuthToken('');
    });
    return () => {
      setUnauthorizedHandler(null);
      unsub();
    };
  }, []);

  const refreshToken = async () => {
    if (!auth.currentUser) return '';
    const idToken = await auth.currentUser.getIdToken(true);
    setToken(idToken);
    setAuthToken(idToken);
    return idToken;
  };

  const signIn = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const idToken = await auth.currentUser.getIdToken();
      setAuthToken(idToken);
      setToken(idToken);
      setUser(auth.currentUser);
      // Mark onboarding as complete for returning users
      localStorage.setItem('mp:onboarded', '1');
    } catch (err) {
      const code = err?.code || '';
      const invalidCred = code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials';
      const tooManyRequests = code === 'auth/too-many-requests';
      const message = invalidCred
        ? 'The sign-in details are incorrect. Please try again.'
        : tooManyRequests
          ? 'Too many attempts. Try again later'
        : (err?.message || 'Unable to sign in. Please try again.');
      throw new Error(message);
    }
  };

  const signUp = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
    const idToken = await cred.user.getIdToken();
    setAuthToken(idToken);
    setToken(idToken);
    setUser(cred.user);
    // New users must complete onboarding
    localStorage.removeItem('mp:onboarded');
  };

  const signInWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
    const idToken = await auth.currentUser.getIdToken();
    setAuthToken(idToken);
    setToken(idToken);
    setUser(auth.currentUser);
  };

  const signInWithApple = async () => {
    // On iOS/Safari you may prefer redirect; popup is fine for most browsers
    const provider = appleProvider;
    if (/Safari\//.test(navigator.userAgent) && !/Chrome\//.test(navigator.userAgent)) {
      await signInWithRedirect(auth, provider);
    } else {
      await signInWithPopup(auth, provider);
    }
    const idToken = await auth.currentUser.getIdToken();
    setAuthToken(idToken);
    setToken(idToken);
    setUser(auth.currentUser);
  };

  const sendReset = async (email) => {
    // Firebase's hosted reset page shows a "Continue" button to this URL once the new password is
    // saved; without it the user is left stranded there. The domain must be in Firebase Auth's
    // authorised domains (localhost is by default).
    await sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/auth/signin` });
  };

  const signOut = async () => {
    await fbSignOut(auth);
    clearTrialInterstitialAcknowledgements();
    // Sign-up markers belong to the account that set them; never carry them to the next sign-in.
    clearOnboardingInProgress();
    clearTrialChoice();
    setUser(null);
    setToken('');
    setAuthToken('');
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      signIn,
      signUp,
      signOut,
      sendReset,
      refreshToken,
      signInWithGoogle,
      signInWithApple,
    }),
    [user, token, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
